import React, { useSyncExternalStore } from "react";
import { 
  EphemeralWsRequest, 
  EphemeralWsRequestOptions, 
  startEphemeralWsRequest 
} from "@sharpana/shared/utils/EphemeralWsRequest";

/**
 * Hook for managing ephemeral WebSocket requests
 * 
 * Each call to start() creates a new WebSocket connection that automatically
 * closes when the stream finishes or is canceled.
 */
export function useEphemeralWsRequest<TIn, TOut>() {
  const [request, setRequest] = React.useState<EphemeralWsRequest<TIn, TOut> | null>(null);

  // Drive re-renders for *this* request only
  const subscribe = React.useCallback((cb: () => void) => {
    if (!request) return () => {};
    return request.subscribe(() => cb());
  }, [request]);

  const getSnapshot = React.useCallback(() => {
    return request?.lastMessage ?? null;
  }, [request]);

  const lastMessage = useSyncExternalStore(subscribe, getSnapshot);

  // auto-cleanup on unmount
  React.useEffect(() => {
    return () => request?.cancel();
  }, [request]);

  const start = React.useCallback((opts: EphemeralWsRequestOptions<TIn>) => {
    const newRequest = startEphemeralWsRequest<TIn, TOut>(opts);
    setRequest(newRequest);
    return newRequest;
  }, []);

  const cancel = React.useCallback(() => {
    request?.cancel();
    setRequest(null);
  }, [request]);

  return {
    request,             // null until started
    lastMessage,         // reactive to this request only
    start,               // creates new request
    cancel,              // cancels current request
    state: request?.state ?? "finished",
    messages: request?.messages ?? [],
  };
}
